import React, { LegacyRef, createRef, useEffect, useState, useLayoutEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RouteComponentProps } from "react-router"
import {
  mdiMessageAlert,
  mdiRefresh,
  mdiPlus,
  mdiFilter,
  mdiCheckCircle,
  mdiClockOutline,
  mdiAlertCircle,
  mdiFileDocumentMultiple,
  mdiPlaylistPlus,
  mdiClipboardList,
} from "@mdi/js"
import Icon from "@mdi/react"
import { cloneDeep } from "lodash"
import Radium from "radium"
import { Table } from "simplygrow-ui"

import { ColorPalette } from "../../config/colors"
import { ActionType } from "../../store/actions/actions"
import {
  normaliseStringForComparison,
  removeUnderScores,
  titleCase,
  toUpperCaseCustom,
  toValidPfDate,
  writeRemoteFilesToLocalStorage,
} from "../../utils"
import {
  EmploymentStatus,
  UiCategoryEnum,
  TrashStatus,
  AssociationId,
  ColumnConfig,
  NavMenuAccess,
  AsyncTaskTypesEnum,
} from "../../types"
import { BulkDisciplineActionSchema, DisciplineActionsEnum } from "../../types/disciplineTypes"
import { CustomFilterState } from "../../components/Tables/DataTable/DataTable"
import NavBar from "../../components/Navigation/NavBar"
import SectionHeader from "../../components/Headings/SectionHeaderPrimary"
import LoadingModal from "../../components/Modals/LoadingModal"
import AlertModalOneButton from "../../components/Modals/AlertModalOneButton"
import AlertModalTwoButton from "../../components/Modals/AlertModalTwoButton"
import DocumentViewerModal from "../../components/Modals/DocumentViewerModal"
import DownloadQueueModal from "../../components/Modals/DownloadQueueModal"
import LabelCollector from "../../components/Modals/LabelCollector"
import SideMenu from "../../components/Navigation/SideMenu"
import { SideMenuOptions } from "../../components/SideMenus/SideMenuOptions"
import { SideMenuTitle } from "../../components/SideMenus/SideMenuTitle"
import { PeopleFlowCombinedReducer } from "../../store"
import { SessionService, ProfileService, FilterService, TableService, ConfigService } from "../../services"
import { getBatches, deleteBatch } from "../../providers/endpointDataProvider/asyncTasks"
import { getRemoteFile } from "../../providers/remoteDataProvider"
import { FilePathProvider } from "../../providers/filePathProvider"
import { ActionButtonType, Toolbar } from "../../components/GeneralUI/Toolbar"
import { AssociationSettingsRepository, ProfileRepository } from "../../repositories"
import { Profile } from "../../models/profile/profile"
import { DisciplineService } from "../../services/disciplineService"
import ActionFlows from "../../components/GeneralUI/ActionFlows/ActionFlows"
import DisciplineActionTile from "../../components/BaseComponents/Buttons/DisciplineTile"
import ButtonStatus from "../../components/BaseComponents/Buttons/ButtonStatus"
import { DisciplineAction } from "../../models/discipline/discipline.action"
import { DisciplineRecord } from "../../models/discipline/disciplineRecord"
import Modal from "../../components/Modals/Modal"

const sessionService = new SessionService()
const profileService = new ProfileService()
const filterService = new FilterService()
const tableService = new TableService()
const disciplineService = new DisciplineService()
const configService = new ConfigService()

interface BulkDisciplineProps extends RouteComponentProps {}

const sectionHeaderRef: LegacyRef<any> = createRef()
const primaryTableRef: LegacyRef<HTMLDivElement> = createRef()

const BulkDiscipline = (props: BulkDisciplineProps) => {
  const initialModalState = {
    loadingModalOpen: false,
    warningModalOpen: false,
    loadingModalMessage: "",
    loadingModalAuxMessage: "",
    warningModalHeader: "",
    warningModalMessage: "",
    labelCollectorOpen: false,
  }

  const [modalState, setModalState] = useState(initialModalState)
  const [sideMenuVisible, setSideMenuVisible] = useState(true)
  const [selectedFilter, setSelectedFilter] = useState<string>("ALL")
  const [viewMode, setViewMode] = useState<"BULK_ACTIONS" | "PROFILE_SELECTION" | "PRESCRIPTION_REVIEW">("BULK_ACTIONS")
  const [selectedOffence, setSelectedOffence] = useState<string>("")
  const [profileData, setProfileData] = useState<any[]>([])
  const [searchString, setSearchString] = useState("")
  const [filterConfig, setFilterConfig] = useState<{ key: string; label: string }[]>([
    {
      key: "idPassport",
      label: "ID/Passport",
    },
  ])
  const [tableHeight, setTableHeight] = useState<number>(500)
  const [tableReloadTrigger, setTableReloadTrigger] = useState(0)
  const [selectedProfilePks, setSelectedProfilePks] = useState<string[]>([])
  const [tableColumnConfig, setTableColumnConfig] = useState<ColumnConfig[]>([])
  const [cacheTimestamp, setCacheTimestamp] = useState<number>(0)
  const [checkTimestamp, setCheckTimestamp] = useState<number>(Date.now())
  const [offenceOptions, setOffenceOptions] = useState<string[]>([])
  const [bulkActionTasks, setBulkActionTasks] = useState<BulkDisciplineActionSchema[]>([])
  const [bulkActionTasksTableData, setBulkActionTasksTableData] = useState<any[]>([])
  const [prescriptionReviewData, setPrescriptionReviewData] = useState<any[]>([])
  const [actionFlowsModalOpen, setActionFlowsModalOpen] = useState(false)
  const [selectedProfileForAction, setSelectedProfileForAction] = useState<string | null>(null)
  const [actionFlowsData, setActionFlowsData] = useState<Record<string, any>>({})
  const [userSelectedActions, setUserSelectedActions] = useState<Record<string, string>>({})
  const [selectedBulkActionIds, setSelectedBulkActionIds] = useState<string[]>([])
  const [downloadQueueModalOpen, setDownloadQueueModalOpen] = useState(false)
  const [asyncTaskQueue, setAsyncTaskQueue] = useState<any[]>([])
  const [isUpdatingQueue, setIsUpdatingQueue] = useState(false)
  const [itemsBeingGenerated, setItemsBeingGenerated] = useState<any[]>([])
  const [itemsDownloading, setItemsDownloading] = useState<any[]>([])
  const [documentViewerModalOpen, setDocumentViewerModalOpen] = useState(false)
  const [selectedDocumentNames, setSelectedDocumentNames] = useState<string[]>([])
  const [selectedDocumentProfilePk, setSelectedDocumentProfilePk] = useState<string | null>(null)
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0)
  const [currentDocumentUrl, setCurrentDocumentUrl] = useState<string | null>(null)
  const [alertTwoButtonModalOpen, setAlertTwoButtonModalOpen] = useState(false)
  const [pendingActionForModal, setPendingActionForModal] = useState<DisciplineAction | null>(null)

  const userIdPassport = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.idPassport)
  const password = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.password)
  const associationRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.associationRepo,
  ) as AssociationSettingsRepository
  const profileRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.profileRepo,
  ) as ProfileRepository
  const selectedAssociation = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.selectedAssociation,
  )
  const selectedCohort = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.selectedCohort)
  const currentScreen = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.currentScreen)
  const navMenuAccess = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.navMenuAccess)

  const dispatch = useDispatch()
  const updateState = (data: any) => dispatch({ type: ActionType.UPDATE_STATE, data })

  useEffect(() => {
    if (!selectedAssociation) {
      return
    }
    const allOffenceOptions = associationRepo.getAllDisciplinaryOffenceOptions(selectedAssociation, selectedCohort)

    setModalState({
      ...modalState,
      loadingModalOpen: true,
      loadingModalMessage: "Loading bulk discipline actions...",
      loadingModalAuxMessage: "",
    })

    // const { disciplineHistory, activeActionFlows, catchAllFlow } = selectedProfileEntity?.getDisciplineFlows(
    //   associationEntity,
    //   disciplineService,
    //   configService,
    //   selectedCohort,
    // )

    setOffenceOptions(allOffenceOptions)
    setSideMenuVisible(true)
    setViewMode("BULK_ACTIONS")
    setSelectedFilter("ALL")
    setSearchString("")
    updateState({
      profilePic: "",
      currentSection: UiCategoryEnum.BULK_ACTIONS,
      currentScreen: UiCategoryEnum.DISCIPLINE,
    })
    loadDisciplineTable()
    const initializeCacheTimestamp = async () => {
      const timestamp = await profileRepo.getProfilesDownloadedMs(selectedCohort)
      setCacheTimestamp(timestamp)
    }
    initializeCacheTimestamp()
    startTimeStampUpdateInterval()
  }, [selectedAssociation, selectedCohort])

  useLayoutEffect(() => {
    if (!primaryTableRef.current) {
      return
    }
    const tableTopY = primaryTableRef.current.getBoundingClientRect().top
    const browserWindowHeight = window.innerHeight
    setTableHeight(browserWindowHeight - tableTopY)
  }, [primaryTableRef.current])

  const closeModals = () => {
    setModalState(initialModalState)
    setDownloadQueueModalOpen(false)
  }

  const errorHandler = (error: any): void => {
    let header = "Warning"
    let warningModalMessage = ""

    if ("message" in error) {
      warningModalMessage = `The following error message was returned: \n\n 
        ${error.message}.\n\n Contact tech support to report the issue.`
    } else {
      warningModalMessage =
        "We encountered a problem. Refresh the page and try again. If you are still unsuccessful, contact tech support for assistance"
    }

    setModalState({
      ...initialModalState,
      warningModalOpen: true,
      warningModalHeader: header,
      warningModalMessage,
    })
  }

  const openLabelCollector = () => {
    setModalState({
      ...modalState,
      labelCollectorOpen: true,
    })
  }

  const closeLabelCollector = () => {
    setModalState({
      ...modalState,
      labelCollectorOpen: false,
    })
  }

  const handleOffenceSelection = (offence: string) => {
    // Find the original offence from offenceOptions by comparing normalized strings
    let originalOffence = offence
    for (const option of offenceOptions) {
      if (normaliseStringForComparison(removeUnderScores(option)) === normaliseStringForComparison(offence)) {
        originalOffence = option
        break
      }
    }
    setSelectedOffence(originalOffence)
    closeLabelCollector()
    setViewMode("PROFILE_SELECTION")
    setSideMenuVisible(false)
    setSearchString("")
    loadProfileSelectionTable()
  }

  const initialiseTableColumnConfig = (associationRepo: AssociationSettingsRepository, association: AssociationId) => {
    const columnConfig = associationRepo.getProfileTableColumnConfig(association)
    const clonedColumnConfig = cloneDeep(columnConfig)
    return clonedColumnConfig.slice(0, 5)
  }

  const startTimeStampUpdateInterval = () => {
    setCheckTimestamp(Date.now())
    setTimeout(startTimeStampUpdateInterval, 60000)
  }

  const refreshAllProfiles = async () => {
    const loadingModalMessage = "Refreshing profiles..."
    try {
      setModalState({
        ...initialModalState,
        loadingModalOpen: true,
        loadingModalMessage,
        loadingModalAuxMessage: "This may take a few minutes",
      })

      const IS_FRESH_DOWNLOAD = false
      await profileService.downloadAssociationProfiles(
        IS_FRESH_DOWNLOAD,
        sessionService,
        associationRepo,
        profileRepo,
        { username: userIdPassport, password },
        selectedAssociation,
        selectedCohort,
        (progress: string) => {
          setModalState({
            ...initialModalState,
            loadingModalOpen: true,
            loadingModalMessage,
            loadingModalAuxMessage: progress,
          })
        },
      )
      const newCacheTimestamp = await profileRepo.getProfilesDownloadedMs(selectedCohort)
      setCacheTimestamp(newCacheTimestamp)

      // Update the table with the newly downloaded profiles
      loadProfileSelectionTable()
      closeModals()
    } catch (error: any) {
      errorHandler(error)
    }
  }

  const loadProfileSelectionTable = async () => {
    const extractTargetData = (
      tableColumnConfig: ColumnConfig[],
      filterConfig: { key: string; label: string }[],
      profileFilters?: CustomFilterState[],
    ) => {
      const allAssociationProfileEntities = profileRepo.getAllProfileEntities()
      if (!allAssociationProfileEntities) {
        return
      }
      // Get only EMPLOYEE profiles for bulk discipline
      const allProfilesToRender = profileRepo.getTargetProfiles(
        [selectedCohort],
        [EmploymentStatus.EMPLOYEE],
        [TrashStatus.NONE],
      )
      let profileData = packageProfileDisplayData(Object.values(allProfilesToRender), tableColumnConfig, filterConfig)
      if (profileFilters) {
        profileData = filterService.applyCustomFilters(profileData, profileFilters)
      }
      return profileData
    }

    try {
      const tableColumnConfig = initialiseTableColumnConfig(associationRepo, selectedAssociation)
      const filterConfig = associationRepo.getProfileFilterConfig(selectedAssociation)
      setFilterConfig(filterConfig)
      setTableColumnConfig(tableColumnConfig)

      const profileData = extractTargetData(tableColumnConfig, filterConfig)
      if (!profileData) {
        return
      }
      setProfileData(profileData)
      setTableReloadTrigger(Date.now())
    } catch (error: any) {
      errorHandler(error)
    }
  }

  const loadDisciplineTable = async () => {
    try {
      setModalState({
        ...modalState,
        loadingModalOpen: true,
        loadingModalMessage: "Loading bulk discipline actions...",
        loadingModalAuxMessage: "",
      })
      const { tableData } = await disciplineService.loadBulkDisciplineTableData(selectedAssociation, {
        username: userIdPassport,
        password,
      })

      // Filter tasks to only include profiles from the selected cohort
      const cohortFilteredTasks = tableData.filter((task: BulkDisciplineActionSchema) => {
        const profileEntity = profileRepo.getProfileEntity(task.profilePk)
        if (!profileEntity) {
          return false
        }
        const profileCohorts = profileEntity.getCohorts()
        return profileCohorts.includes(selectedCohort)
      })

      setBulkActionTasks(cohortFilteredTasks)
      setBulkActionTasksTableData(cohortFilteredTasks)
      // Set column config based on default ALL filter
      const columnConfig = buildBulkActionsColumnConfig("ALL")
      setTableColumnConfig(columnConfig)
      setTableReloadTrigger(Date.now())
      closeModals()
    } catch (error: any) {
      errorHandler(error)
    }
  }

  const loadPrescriptionReviewTable = async (customSelectedActions?: Record<string, string>) => {
    try {
      const tableColumnConfig = initialiseTableColumnConfig(associationRepo, selectedAssociation)
      const filterConfig = associationRepo.getProfileFilterConfig(selectedAssociation)

      // Get only selected profiles
      const selectedProfileEntities = selectedProfilePks
        .map((pk) => profileRepo.getProfileEntity(pk))
        .filter((entity): entity is Profile => entity !== undefined)

      if (selectedProfileEntities.length === 0) {
        return
      }

      const associationEntity = associationRepo.getAssociationSettingsEntity(selectedAssociation)
      if (!associationEntity) {
        return
      }

      // Use provided selections or fall back to state
      const selectionsToUse = customSelectedActions || userSelectedActions

      // Build table data with prescribed actions
      const prescriptionData = selectedProfileEntities.map((profileEntity) => {
        const profilePk = profileEntity.getPk()
        let returnData = { id: profilePk } as Record<string, string>

        // Add filter config data
        filterConfig.forEach((configItem) => {
          const value = profileEntity.getGeneralDataValue(configItem.key, "-")
          returnData[configItem.key] = value || "-"
        })

        // Add table column config data
        tableColumnConfig.forEach((configItem) => {
          let value = profileEntity.getGeneralDataValue(configItem.key, "-")
          if (Array.isArray(value)) {
            value = value.join("; ")
          }
          const transformedValue = toUpperCaseCustom(value)
          returnData[configItem.key] = transformedValue || "-"
        })

        // Check if user has manually selected an action for this profile
        let actionToDisplay = selectionsToUse[profilePk]

        if (!actionToDisplay) {
          // Get prescribed action if no manual selection
          const { activeActionFlows, catchAllFlow } = profileEntity.getDisciplineFlows(
            associationEntity,
            disciplineService,
            configService,
            selectedCohort,
          )
          const contextData = profileEntity.getGeneralData()
          actionToDisplay = disciplineService.getNextPrescribedAction(
            activeActionFlows,
            catchAllFlow,
            selectedOffence,
            configService,
            contextData,
            associationEntity,
            selectedCohort,
          )
        }

        returnData.prescribedAction = actionToDisplay ? removeUnderScores(actionToDisplay) : "-"

        return returnData
      })

      // Add prescribed action column to table config
      const updatedTableColumnConfig = [
        ...tableColumnConfig,
        { key: "prescribedAction", label: "Prescribed Action", sizeFactor: 2 },
      ]

      setPrescriptionReviewData(prescriptionData)
      setTableColumnConfig(updatedTableColumnConfig)
      setTableReloadTrigger(Date.now())
    } catch (error: any) {
      errorHandler(error)
    }
  }

  const packageProfileDisplayData = (
    profilesEntitiesToRender: Profile[],
    tableColumnConfig: ColumnConfig[],
    filterConfig: { key: string; label: string }[],
  ): any[] => {
    return profilesEntitiesToRender.map((profileEntity: any) => {
      const profilePk = profileEntity.getPk()
      let returnData = { id: profilePk } as Record<string, string>
      filterConfig.forEach((configItem) => {
        const value = profileEntity.getGeneralDataValue(configItem.key, "-")
        returnData[configItem.key] = value || "-"
      })
      tableColumnConfig.forEach((configItem) => {
        let value = profileEntity.getGeneralDataValue(configItem.key, "-")
        if (Array.isArray(value)) {
          value = value.join("; ")
        }
        const transformedValue = toUpperCaseCustom(value)
        returnData[configItem.key] = transformedValue || "-"
      })
      return returnData
    })
  }

  const renderDataIcon = (params: any) => {
    const handleCaptureClick = () => {
      console.log("Capture clicked for row:", params.row)
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}>
        <ButtonStatus
          hasTriggered={false}
          triggeredLabel={"CAPTURED"}
          notTriggeredLabel={"CAPTURE"}
          onClick={handleCaptureClick}
        />
      </div>
    )
  }

  const buildBulkActionsColumnConfig = (filter: string): ColumnConfig[] => {
    const baseColumns: ColumnConfig[] = [
      { key: "name", label: "Name", sizeFactor: 1 },
      { key: "surname", label: "Surname", sizeFactor: 1 },
      { key: "idPassport", label: "ID/Passport", sizeFactor: 1 },
      { key: "offence", label: "Offence", sizeFactor: 2 },
      { key: "action", label: "Action", sizeFactor: 2 },
    ]

    baseColumns.push(
      { key: "creationDate", label: "Creation Date", sizeFactor: 2 },
      { key: "expiryDate", label: "Expiry Date", sizeFactor: 2 },
      { key: "lastModifiedDate", label: "Last Modified Date", sizeFactor: 2 },
    )

    // Only add status column for ALL filter
    if (filter === "ALL") {
      baseColumns.push({ key: "status", label: "Status", sizeFactor: 2 })
    }

    // Add data icon column for filters other than ALL
    if (filter !== "ALL") {
      baseColumns.push({ key: "data", label: "Data", sizeFactor: 1, renderCell: renderDataIcon })
    }

    return baseColumns
  }

  const handleFilterSelection = (filter: string) => {
    setSelectedFilter(filter)
    const filteredTasks = getFilteredTasksForFilter(filter)
    setBulkActionTasksTableData(filteredTasks)
    setSelectedBulkActionIds([])
    const columnConfig = buildBulkActionsColumnConfig(filter)
    setTableColumnConfig(columnConfig)
    setTableReloadTrigger(Date.now())
  }

  const getFilteredTasksForFilter = (filter: string) => {
    if (filter === "ALL") {
      return bulkActionTasks
    }
    if (filter === "IN PROGRESS") {
      return bulkActionTasks.filter((task) => !task.isComplete && !task.isExpired)
    }
    if (filter === "COMPLETE") {
      return bulkActionTasks.filter((task) => task.isComplete)
    }
    if (filter === "EXPIRED") {
      return bulkActionTasks.filter((task) => task.isExpired)
    }
    return bulkActionTasks
  }

  const buildSideMenuComponents = () => {
    const filterOptions = ["ALL", "IN PROGRESS", "COMPLETE", "EXPIRED"]

    const getOptionIcon = (option: string) => {
      if (option === "ALL") {
        return mdiFilter
      }
      if (option === "IN PROGRESS") {
        return mdiClockOutline
      }
      if (option === "COMPLETE") {
        return mdiCheckCircle
      }
      if (option === "EXPIRED") {
        return mdiAlertCircle
      }
      return mdiFilter
    }

    const getOptionStyle = (option: string) => {
      return {
        fontSize: "smaller",
        maxWidth: "100%",
        backgroundColor: selectedFilter === option ? ColorPalette.BUTTON_ACTIVE_GREY : ColorPalette.OFF_WHITE_LIGHT,
      }
    }

    return (
      <div style={styles.sideMenuContainer}>
        <SideMenuTitle>Disciplinary Tasks</SideMenuTitle>

        <div style={styles.sideMenuIconContainer}>
          <Icon path={mdiClipboardList} size={4.2} color={ColorPalette.PRIMARY_BLUE} />
        </div>
        <SideMenuOptions
          options={filterOptions}
          optionLabel={(option: string) => option}
          optionClick={handleFilterSelection}
          optionMdiIconPath={getOptionIcon}
          optionStyle={getOptionStyle}
        />
      </div>
    )
  }

  const searchHandler = (e: React.ChangeEvent<{ value: string }>) => {
    const newSearchString = e.target.value
    setSearchString(newSearchString)
  }

  const buildLastUpdateMessage = () => {
    const cacheAgeInMilliseconds = checkTimestamp - cacheTimestamp

    const millisecondsPerMinute = 1000 * 60
    const millisecondsPerHour = millisecondsPerMinute * 60
    const millisecondsPerDay = millisecondsPerHour * 24
    const daysSinceLastCache = Math.floor(cacheAgeInMilliseconds / millisecondsPerDay)

    if (daysSinceLastCache >= 1) {
      return `Last updated profiles: ${daysSinceLastCache} day${daysSinceLastCache > 1 ? "s" : ""} ago`
    }
    const hoursSinceLastCache = Math.floor(cacheAgeInMilliseconds / millisecondsPerHour)
    if (hoursSinceLastCache >= 1) {
      return `Last updated profiles: ${hoursSinceLastCache} hour${hoursSinceLastCache > 1 ? "s" : ""} ago`
    }
    const minutesSinceLastCache = Math.floor(cacheAgeInMilliseconds / millisecondsPerMinute)
    if (minutesSinceLastCache >= 1) {
      return `Last updated profiles: ${minutesSinceLastCache} minute${minutesSinceLastCache > 1 ? "s" : ""} ago`
    }
    return undefined
  }

  const handleProfileSelection = (selectedPks: string[]) => {
    // Check if any newly selected profiles are suspended
    const newlySelectedPks = selectedPks.filter((pk) => !selectedProfilePks.includes(pk))
    let suspendedProfileName: string | null = null

    // Check each newly selected profile for suspension
    for (const pk of newlySelectedPks) {
      const profileEntity = profileRepo.getProfileEntity(pk)
      if (!profileEntity) {
        continue
      }

      if (profileEntity.isEmployeeSuspended()) {
        const personalInfo = profileEntity.getPersonalInfo()
        suspendedProfileName = `${personalInfo.name} ${personalInfo.surname}`
        break
      }
    }

    // Filter out suspended profiles from the selection
    const validSelectedPks = selectedPks.filter((pk) => {
      const profileEntity = profileRepo.getProfileEntity(pk)
      return profileEntity && !profileEntity.isEmployeeSuspended()
    })

    // Update state with valid profiles only
    setSelectedProfilePks(validSelectedPks)

    // Show warning if a suspended profile was attempted
    if (suspendedProfileName) {
      setModalState({
        ...modalState,
        warningModalOpen: true,
        warningModalHeader: "Profile Suspended",
        warningModalMessage: `${suspendedProfileName} cannot be added as they are suspended.`,
      })
    }
  }

  const handleSubmitSelectedProfiles = async () => {
    if (selectedProfilePks.length === 0) {
      setModalState({
        ...modalState,
        warningModalOpen: true,
        warningModalHeader: "No profiles selected",
        warningModalMessage: "Select at least one profile to add for disciplinary action.",
      })
      return
    }

    try {
      setModalState({
        ...modalState,
        loadingModalOpen: true,
        loadingModalMessage: "Loading prescribed actions...",
        loadingModalAuxMessage: "",
      })

      // Load prescription review table
      await loadPrescriptionReviewTable()

      // Transition to prescription review mode
      setViewMode("PRESCRIPTION_REVIEW")
      setSearchString("")
      closeModals()
    } catch (error: any) {
      errorHandler(error)
    }
  }

  const handleCancelProfileSelection = () => {
    setViewMode("BULK_ACTIONS")
    setSideMenuVisible(true)
    setSelectedProfilePks([])
    setSelectedOffence("")
    setUserSelectedActions({})
    setSearchString("")
    setTableReloadTrigger(Date.now())
    loadDisciplineTable()
  }

  const openActionFlowsModal = async (rowData: Record<string, any>, offenceToUse?: string) => {
    // In BULK_ACTIONS view, rowData.id is the task ID, and profilePk is in rowData.profilePk
    // In other views, rowData.id is the profile PK
    console.log("viewMode: ", viewMode)
    console.log("rowData: ", rowData)
    const profilePk = viewMode === "BULK_ACTIONS" ? rowData.row.profilePk : rowData.id

    try {
      // Show loading modal while re-downloading profile
      setModalState({
        ...modalState,
        loadingModalOpen: true,
        loadingModalMessage: "Loading profile...",
        loadingModalAuxMessage: "Fetching latest data",
      })

      // Re-download the profile to ensure most up-to-date information
      await profileService.refreshProfile(
        selectedAssociation,
        profileRepo,
        { username: userIdPassport, password },
        profilePk,
      )

      // Get the refreshed profile entity
      const profileEntity = profileRepo.getProfileEntity(profilePk)

      if (!profileEntity) {
        setModalState({
          ...modalState,
          warningModalOpen: true,
          warningModalHeader: "Profile Not Found",
          warningModalMessage: "Could not load profile data. Please try again.",
        })
        return
      }

      const associationEntity = associationRepo.getAssociationSettingsEntity(selectedAssociation)
      if (!associationEntity) {
        closeModals()
        return
      }

      // Get discipline flows for the profile
      let { activeActionFlows } = profileEntity.getDisciplineFlows(
        associationEntity,
        disciplineService,
        configService,
        selectedCohort,
      )

      // Determine which offence to use
      const targetOffence = offenceToUse || selectedOffence

      // Ensure an action flow exists for the target offence if in PRESCRIPTION_REVIEW mode
      if (viewMode === "PRESCRIPTION_REVIEW" && targetOffence && !activeActionFlows.hasOwnProperty(targetOffence)) {
        const contextData = profileEntity.getGeneralData()
        activeActionFlows = disciplineService.addActionFlow(
          activeActionFlows,
          targetOffence,
          contextData,
          configService,
          associationEntity,
          selectedCohort,
        )
      }

      // Enhance each flow with correct label metadata
      const enhancedActiveActionFlows = { ...activeActionFlows }
      Object.keys(enhancedActiveActionFlows).forEach((offenceKey) => {
        const flowRecords = enhancedActiveActionFlows[offenceKey]
        let prescribedCount = 0
        let foundRecommended = false

        flowRecords.forEach((record) => {
          const isPrescribed = record.isPrescribedAction()
          const hasUid = !!record.getUid()

          if (isPrescribed) {
            prescribedCount++
            if (!hasUid && !foundRecommended) {
              // First incomplete prescribed action is RECOMMENDED
              record.upsertUiData({ labelText: `${prescribedCount}. RECOMMENDED`, labelNumber: prescribedCount })
              foundRecommended = true
            } else {
              // Other prescribed actions are STANDARD
              record.upsertUiData({ labelText: `${prescribedCount}. STANDARD`, labelNumber: prescribedCount })
            }
          } else {
            // Non-prescribed actions are OPTIONAL
            record.upsertUiData({ labelText: "OPTIONAL", labelNumber: null })
          }
        })
      })

      setActionFlowsData(enhancedActiveActionFlows)
      setSelectedProfileForAction(profilePk)

      // Close loading modal and open action flows modal
      closeModals()
      setActionFlowsModalOpen(true)
    } catch (error: any) {
      errorHandler(error)
    }
  }

  const handleActionFlowClick = async (disciplineAction: DisciplineAction) => {
    const hasUid = !!disciplineAction.uid
    const hasDocs = disciplineAction.docs && disciplineAction.docs.length > 0

    // BULK_ACTIONS or PROFILE_SELECTION views
    if (viewMode === "BULK_ACTIONS" || viewMode === "PROFILE_SELECTION") {
      if (!hasUid) {
        // Action not yet actioned - do nothing (button should be disabled)
        return
      }

      // Action has been actioned - open document viewer
      if (hasDocs && selectedProfileForAction) {
        await loadAndViewDocuments(selectedProfileForAction, disciplineAction.docs)
      }
      return
    }

    // PRESCRIPTION_REVIEW view
    if (viewMode === "PRESCRIPTION_REVIEW") {
      // Only allow clicks on action flows matching the selected offence
      if (disciplineAction.offence !== selectedOffence) {
        return
      }

      if (hasUid) {
        // Action already actioned - show choice modal
        setPendingActionForModal(disciplineAction)
        setAlertTwoButtonModalOpen(true)
        return
      }

      // Action not yet actioned - proceed with selection (current behavior)
      if (!selectedProfileForAction) {
        return
      }

      // Check if TERMINATE action is selected
      if (disciplineAction.action.toLowerCase() === "terminate") {
        setActionFlowsModalOpen(false)
        setModalState({
          ...modalState,
          warningModalOpen: true,
          warningModalHeader: "Termination Not Allowed",
          warningModalMessage:
            "Termination should be performed using the Bulk Termination feature or on the iPad app, not through bulk discipline.",
        })
        return
      }

      try {
        setModalState({
          ...modalState,
          loadingModalOpen: true,
          loadingModalMessage: "Updating selected action...",
          loadingModalAuxMessage: "",
        })

        // Save the selected action for this profile
        const updatedSelections = {
          ...userSelectedActions,
          [selectedProfileForAction]: disciplineAction.action,
        }
        setUserSelectedActions(updatedSelections)

        // Close the modal
        setActionFlowsModalOpen(false)

        // Reload the prescription review table to show updated action
        // Pass the updated selections directly to avoid async state timing issues
        await loadPrescriptionReviewTable(updatedSelections)

        closeModals()
      } catch (error: any) {
        errorHandler(error)
      }
    }
  }

  const loadAndViewDocuments = async (profilePk: string, docNames: string[]) => {
    if (!docNames || docNames.length === 0) {
      return
    }

    setSelectedDocumentNames(docNames)
    setSelectedDocumentProfilePk(profilePk)
    setCurrentDocumentIndex(0)

    // Load the first document, then open modal
    await loadDocumentAtIndex(profilePk, docNames, 0)
    setDocumentViewerModalOpen(true)
  }

  const loadDocumentAtIndex = async (profilePk: string, docNames: string[], index: number) => {
    try {
      const oldUrl = currentDocumentUrl

      setModalState({
        ...modalState,
        loadingModalOpen: true,
        loadingModalMessage: "Loading document...",
      })

      const docName = docNames[index]
      const filepath = FilePathProvider.getRemoteDocumentFilePath(profilePk, selectedAssociation, docName)
      let doc = await getRemoteFile(filepath, { download: true })
      doc = new Blob([doc.Body], { type: "application/pdf" })
      const docUrl = URL.createObjectURL(doc)

      // Set the new URL
      setCurrentDocumentUrl(docUrl)
      setCurrentDocumentIndex(index)
      closeModals()

      // Revoke old URL after delay
      if (oldUrl) {
        setTimeout(() => {
          URL.revokeObjectURL(oldUrl)
        }, 2000)
      }
    } catch (error: any) {
      errorHandler(error)
    }
  }

  const handleViewExistingDocuments = async () => {
    if (!pendingActionForModal || !selectedProfileForAction) {
      return
    }

    setAlertTwoButtonModalOpen(false)

    const documents = pendingActionForModal.docs
    if (documents && documents.length > 0) {
      await loadAndViewDocuments(selectedProfileForAction, documents)
    }
  }

  const handleIssueNewDiscipline = async () => {
    if (!pendingActionForModal || !selectedProfileForAction) {
      return
    }

    setAlertTwoButtonModalOpen(false)

    // Check if TERMINATE action is selected
    if (pendingActionForModal.action.toLowerCase() === "terminate") {
      setActionFlowsModalOpen(false)
      setModalState({
        ...modalState,
        warningModalOpen: true,
        warningModalHeader: "Termination Not Allowed",
        warningModalMessage:
          "Termination should be performed using the Bulk Termination feature or on the iPad app, not through bulk discipline.",
      })
      return
    }

    try {
      setModalState({
        ...modalState,
        loadingModalOpen: true,
        loadingModalMessage: "Updating selected action...",
        loadingModalAuxMessage: "",
      })

      // Save the selected action for this profile
      const updatedSelections = {
        ...userSelectedActions,
        [selectedProfileForAction]: pendingActionForModal.action,
      }
      setUserSelectedActions(updatedSelections)

      // Close the modal
      setActionFlowsModalOpen(false)

      // Reload the prescription review table to show updated action
      await loadPrescriptionReviewTable(updatedSelections)

      closeModals()
    } catch (error: any) {
      errorHandler(error)
    }
  }

  const handleDocumentViewerClose = () => {
    setDocumentViewerModalOpen(false)
    if (currentDocumentUrl) {
      URL.revokeObjectURL(currentDocumentUrl)
    }
    setCurrentDocumentUrl(null)
    setSelectedDocumentNames([])
    setSelectedDocumentProfilePk(null)
    setCurrentDocumentIndex(0)
  }

  const handleNextDocument = async () => {
    if (!selectedDocumentProfilePk || selectedDocumentNames.length === 0) {
      return
    }

    if (currentDocumentIndex < selectedDocumentNames.length - 1) {
      const nextIndex = currentDocumentIndex + 1
      await loadDocumentAtIndex(selectedDocumentProfilePk, selectedDocumentNames, nextIndex)
    }
  }

  const handlePreviousDocument = async () => {
    if (!selectedDocumentProfilePk || selectedDocumentNames.length === 0) {
      return
    }

    if (currentDocumentIndex > 0) {
      const prevIndex = currentDocumentIndex - 1
      await loadDocumentAtIndex(selectedDocumentProfilePk, selectedDocumentNames, prevIndex)
    }
  }

  const getCurrentDocument = () => {
    if (!currentDocumentUrl) {
      return null
    }
    return { url: currentDocumentUrl }
  }

  const convertDisciplineRecordToAction = (record: DisciplineRecord): DisciplineAction => {
    const rawData = record.getRawData()
    const uiData = record.getUiData()
    const labelText = uiData.labelText || (uiData.isPrescribedAction ? "Prescribed" : "Custom")

    return {
      uid: record.getUid(),
      id: record.getUid(),
      docs: record.getDocFileNames(),
      photos: record.getPhotoFileNames(),
      rawData: rawData,
      custom: uiData.isPrescribedAction ? "" : "custom",
      action: record.getAction(),
      offence: record.getOffence(),
      expiry: toValidPfDate(rawData.expiry || ""),
      actionLabel: labelText,
      date: rawData.dateActioned || "",
      unixDate: rawData.dateActioned ? parseInt(rawData.dateActioned) : 0,
      type: rawData.process || "",
      leftComponent: null,
      rightComponent: null,
    }
  }

  const generateActionComponent = (disciplineRecord: any): JSX.Element => {
    // Convert DisciplineRecord to DisciplineAction
    const disciplineAction = convertDisciplineRecordToAction(disciplineRecord)
    const hasUid = !!disciplineAction.uid
    const isMatchingOffence = disciplineAction.offence === selectedOffence

    // Determine clickability based on view mode
    let isDisabled = false
    let isClickable = false

    if (viewMode === "BULK_ACTIONS" || viewMode === "PROFILE_SELECTION") {
      // Only actions with UIDs (already actioned) are clickable
      isClickable = hasUid
      isDisabled = !hasUid
    } else if (viewMode === "PRESCRIPTION_REVIEW") {
      // In prescription review, only matching offence actions are clickable
      // Actions with UIDs show choice modal, actions without can be selected
      isClickable = isMatchingOffence
      isDisabled = !isMatchingOffence
    }

    const tile = (
      <DisciplineActionTile
        key={`${disciplineAction.action}_${disciplineAction.offence}`}
        active={hasUid}
        action={disciplineAction.action}
        expiry={disciplineAction.expiry}
        customAction={disciplineAction.custom}
        actionLabel={disciplineAction.actionLabel}
        authedRoles={[]}
        onClick={() => {
          if (isClickable) {
            handleActionFlowClick(disciplineAction)
          }
        }}
      />
    )

    if (isDisabled) {
      return (
        <div
          key={`${disciplineAction.action}_${disciplineAction.offence}_wrapper`}
          style={{
            opacity: 0.4,
            pointerEvents: "none",
            cursor: "not-allowed",
          }}>
          {tile}
        </div>
      )
    }

    return tile
  }

  const handleSubmitBulkDisciplineActions = async () => {
    try {
      setModalState({
        ...modalState,
        loadingModalOpen: true,
        loadingModalMessage: "Submitting bulk discipline actions...",
        loadingModalAuxMessage: "This may take a moment...",
      })

      const associationEntity = associationRepo.getAssociationSettingsEntity(selectedAssociation)
      if (!associationEntity) {
        throw new Error("Association not found")
      }

      // Get all selected profiles
      const selectedProfileEntities = selectedProfilePks
        .map((pk) => profileRepo.getProfileEntity(pk))
        .filter((entity): entity is Profile => entity !== undefined)

      // Build discipline actions for each profile
      const now = Date.now()
      const actionsToSubmit: BulkDisciplineActionSchema[] = []
      const skippedProfiles: { name: string; surname: string; reason: string }[] = []

      for (const profileEntity of selectedProfileEntities) {
        const profilePk = profileEntity.getPk()
        const profileGeneralData = profileEntity.getGeneralData()

        // Get the action for this profile (user-selected or prescribed)
        let actionToSubmit = userSelectedActions[profilePk]

        // Get discipline flows to determine expiry
        const { activeActionFlows, catchAllFlow } = profileEntity.getDisciplineFlows(
          associationEntity,
          disciplineService,
          configService,
          selectedCohort,
        )

        if (!actionToSubmit) {
          // Calculate prescribed action
          const contextData = profileEntity.getGeneralData()
          actionToSubmit = disciplineService.getNextPrescribedAction(
            activeActionFlows,
            catchAllFlow,
            selectedOffence,
            configService,
            contextData,
            associationEntity,
            selectedCohort,
          )
        }
        if (actionToSubmit) {
          const isDuplicate = bulkActionTasks.some((existingTask: BulkDisciplineActionSchema) => {
            return (
              existingTask.profilePk === profilePk &&
              normaliseStringForComparison(existingTask.offence) === normaliseStringForComparison(selectedOffence) &&
              normaliseStringForComparison(existingTask.action) === normaliseStringForComparison(actionToSubmit)
            )
          })

          if (isDuplicate) {
            skippedProfiles.push({
              name: profileGeneralData.name || "",
              surname: profileGeneralData.surname || "",
              reason: `Already has pending ${removeUnderScores(actionToSubmit)} for ${removeUnderScores(
                selectedOffence,
              )}`,
            })
            continue
          }

          const expiryUnixMs = disciplineService.getExpiryUnixMs(
            associationEntity,
            profileEntity,
            configService,
            now,
            selectedOffence,
            actionToSubmit,
          )
          const expiryValue = toValidPfDate(String(expiryUnixMs))

          actionsToSubmit.push({
            id: `${now}-${profilePk}`,
            profilePk,
            name: profileGeneralData.name || "",
            surname: profileGeneralData.surname || "",
            idPassport: profileGeneralData.idPassport || "",
            action: actionToSubmit as DisciplineActionsEnum,
            offence: selectedOffence,
            isComplete: false,
            isExpired: false,
            expiry: expiryValue,
            creationUnixMs: String(now),
            lastModifiedUnixMs: String(now),
            contextualData: {},
          })
        }
      }

      console.log("actionsToSubmit", JSON.stringify(actionsToSubmit))
      // Check if all profiles were skipped
      if (actionsToSubmit.length === 0) {
        const skippedList = skippedProfiles
          .map((profile) => `${profile.name} ${profile.surname}: ${profile.reason}`)
          .join("\n")

        closeModals()
        setModalState({
          ...modalState,
          warningModalOpen: true,
          warningModalHeader: "No Actions to Submit",
          warningModalMessage: `All selected profiles were skipped because they already have these discipline actions:\n\n${skippedList}`,
        })
        return
      }

      // Submit discipline actions to API
      await disciplineService.submitBulkDisciplineActions(selectedAssociation, actionsToSubmit, {
        username: userIdPassport,
        password,
      })

      // Add submitted actions to state
      console.log("Successfully submitted bulk discipline actions:", actionsToSubmit)

      const newBulkActionTasks = actionsToSubmit

      // Add new tasks to the existing bulk action tasks
      const updatedBulkActionTasks = [...bulkActionTasks, ...newBulkActionTasks]
      setBulkActionTasks(updatedBulkActionTasks)

      // Format new tasks for display (add date strings, status, and remove underscores)
      const formattedNewTasks = newBulkActionTasks.map((task) => {
        let status = "IN PROGRESS"
        if (task.isComplete) {
          status = "COMPLETE"
        } else if (task.isExpired) {
          status = "EXPIRED"
        }

        return {
          ...task,
          offence: removeUnderScores(task.offence),
          action: removeUnderScores(task.action),
          status,
          expiryDate: toValidPfDate(task.expiry) || "-",
          creationDate: toValidPfDate(task.creationUnixMs) || "-",
          lastModifiedDate: toValidPfDate(task.lastModifiedUnixMs) || "-",
        }
      })

      // Apply current filter to new tasks
      const filteredNewTasks = formattedNewTasks.filter((task) => {
        if (selectedFilter === "ALL") return true
        if (selectedFilter === "IN PROGRESS") return !task.isComplete && !task.isExpired
        if (selectedFilter === "COMPLETE") return task.isComplete
        if (selectedFilter === "EXPIRED") return task.isExpired
        return true
      })

      // Add filtered new tasks to current table data
      const updatedFilteredTasks = [...bulkActionTasksTableData, ...filteredNewTasks]
      setBulkActionTasksTableData(updatedFilteredTasks)

      // Reset column config to bulk actions columns (default to ALL filter)
      const bulkActionsColumnConfig = buildBulkActionsColumnConfig("ALL")
      setTableColumnConfig(bulkActionsColumnConfig)
      setSelectedFilter("ALL")

      // Reset state and return to bulk actions view
      setViewMode("BULK_ACTIONS")
      setSideMenuVisible(true)
      setSelectedProfilePks([])
      setSelectedOffence("")
      setSearchString("")
      setUserSelectedActions({})
      setTableReloadTrigger(Date.now())

      closeModals()

      // Build success message with skip information
      let successMessage = `Successfully submitted ${actionsToSubmit.length} disciplinary action(s).`

      if (skippedProfiles.length > 0) {
        const skippedList = skippedProfiles
          .map((profile) => `${profile.name} ${profile.surname}: ${profile.reason}`)
          .join("\n")
        successMessage += `\n\n${skippedProfiles.length} profile(s) were skipped due to duplicate actions:\n\n${skippedList}`
      }

      setModalState({
        ...modalState,
        warningModalOpen: true,
        warningModalHeader: "Actions Submitted",
        warningModalMessage: successMessage,
      })
    } catch (error: any) {
      errorHandler(error)
    }
  }

  const handleGenerateDocs = async () => {
    if (selectedBulkActionIds.length === 0) {
      setModalState({
        ...modalState,
        warningModalOpen: true,
        warningModalHeader: "No Items Selected",
        warningModalMessage: "Select at least one item for which to generate documents.",
      })
      return
    }

    try {
      setModalState({
        ...modalState,
        loadingModalOpen: true,
        loadingModalMessage: "Generating bulk discipline documents...",
        loadingModalAuxMessage: "This may take a moment...",
      })

      const authCredentials = { username: userIdPassport, password }
      const batchLabel = `Bulk Discipline - ${userIdPassport} - ${new Date().toLocaleString()}`

      await disciplineService.triggerBulkDisciplineDocGeneration(
        selectedAssociation,
        selectedBulkActionIds,
        batchLabel,
        authCredentials,
      )

      // Clear selection
      setSelectedBulkActionIds([])

      // Open the queue modal (this will close the loading modal)
      await updateQueueData(true)
    } catch (error) {
      console.error("Error generating bulk discipline documents:", error)
      setModalState({
        ...modalState,
        warningModalOpen: true,
        warningModalHeader: "Generation Failed",
        warningModalMessage: "Failed to generate documents. Please try again.",
      })
    }
  }

  const updateQueueData = async (showModal?: boolean) => {
    if (showModal) {
      setModalState(initialModalState)
      setDownloadQueueModalOpen(true)
      setIsUpdatingQueue(true)
    }

    try {
      const token = await SessionService.prepareAuthTokens(userIdPassport, password)
      const response = await getBatches(selectedAssociation, AsyncTaskTypesEnum.BULK_DISCIPLINE_ALL, token)
      const asyncTasks = response.asyncTasks || []

      // Transform async tasks to download queue item format
      const queueItems = DisciplineService.asyncTasksToDownloadQueueItems(asyncTasks)

      setAsyncTaskQueue(queueItems)
      setIsUpdatingQueue(false)
    } catch (error) {
      console.error("Error fetching queue data:", error)
      setIsUpdatingQueue(false)
      setModalState({
        ...modalState,
        warningModalOpen: true,
        warningModalHeader: "Error",
        warningModalMessage: "Failed to fetch download queue. Please try again.",
      })
    }
  }

  const handleDownloadFile = async (filePath: string, queueId: string) => {
    try {
      console.log("filePath", filePath)
      // Check if already downloading
      if (itemsDownloading.length && !itemsDownloading.includes(queueId)) {
        setModalState({
          ...modalState,
          warningModalOpen: true,
          warningModalHeader: "Download in Progress",
          warningModalMessage: "Please wait for the current download to complete.",
        })
        return
      }

      // Check if file path is available
      if (!filePath) {
        // Close queue modal and show warning
        setDownloadQueueModalOpen(false)
        setModalState({
          ...modalState,
          warningModalOpen: true,
          warningModalHeader: "File Generation Failed",
          warningModalMessage: "There was a problem generating the file. The task has been removed from the queue.",
        })
        // Remove from queue even if file generation failed
        const token = await SessionService.prepareAuthTokens(userIdPassport, password)
        await deleteBatch(selectedAssociation, queueId, token)
        await updateQueueData(false)
        return
      }

      // Add to downloading list
      setItemsDownloading([...new Set([...itemsDownloading, queueId])])

      // Download the file
      const filePathElements = filePath.split("/")
      const filename = filePathElements[filePathElements.length - 1]
      await writeRemoteFilesToLocalStorage([{ docPath: filePath, filename }])

      // Remove from downloading list
      setItemsDownloading(itemsDownloading.filter((id) => id !== queueId))

      // Remove from queue
      const token = await SessionService.prepareAuthTokens(userIdPassport, password)
      await deleteBatch(selectedAssociation, queueId, token)

      // Refresh queue
      await updateQueueData(false)
    } catch (error) {
      console.error("Error downloading file:", error)
      setItemsDownloading(itemsDownloading.filter((id) => id !== queueId))
      setModalState({
        ...modalState,
        warningModalOpen: true,
        warningModalHeader: "Download Failed",
        warningModalMessage: "Failed to download file. Please try again.",
      })
    }
  }

  const buildToolbarConfig = (navMenuAccess: NavMenuAccess) => {
    let config: ActionButtonType[] = []
    let navButtons: any = {}

    if (viewMode === "PROFILE_SELECTION") {
      navButtons = {
        left: {
          label: "BACK",
          onClick: handleCancelProfileSelection,
        },
        right: {
          label: `ADD ${selectedProfilePks.length} |\u00A0\u00A0\u00A0${removeUnderScores(selectedOffence)}`,
          onClick: handleSubmitSelectedProfiles,
        },
      }

      config = [
        {
          iconPath: mdiRefresh,
          iconColor: ColorPalette.PRIMARY_BLUE,
          onClick: () => refreshAllProfiles(),
          label: "REFRESH",
          title: "Reload profile data",
          disabled: false,
        },
      ]

      if (cacheTimestamp !== 0) {
        const label = buildLastUpdateMessage()
        if (label) {
          config.unshift({
            iconPath: mdiMessageAlert,
            iconColor: ColorPalette.SECONDARY_TEXT,
            onClick: () => undefined,
            disabled: true,
            label: label,
          })
        }
      }
    } else if (viewMode === "PRESCRIPTION_REVIEW") {
      navButtons = {
        left: {
          label: "BACK",
          onClick: () => {
            setViewMode("PROFILE_SELECTION")
            setSearchString("")
            loadProfileSelectionTable()
          },
        },
        right: {
          label: "SUBMIT",
          onClick: async () => {
            await handleSubmitBulkDisciplineActions()
          },
        },
      }
    } else {
      config = []

      // Add GENERATE DOCS button when IN PROGRESS filter is selected
      if (selectedFilter === "IN PROGRESS") {
        config.push({
          iconPath: mdiFileDocumentMultiple,
          iconColor: ColorPalette.PRIMARY_BLUE,
          onClick: handleGenerateDocs,
          label: "GENERATE DOCS",
          title: "Generate documents for selected actions",
          disabled: false,
        })
      }

      // Add VIEW QUEUE button (always visible)
      config.push({
        iconPath: mdiPlaylistPlus,
        iconColor: ColorPalette.PRIMARY_BLUE,
        onClick: () => updateQueueData(true),
        label: "VIEW QUEUE",
        title: "View document generation queue",
        disabled: false,
      })

      // Add ADD PEOPLE button (always visible)
      config.push({
        iconPath: mdiPlus,
        iconColor: ColorPalette.PRIMARY_BLUE,
        onClick: () => openLabelCollector(),
        label: "ADD PEOPLE",
        title: "Add people",
        disabled: false,
      })
    }

    return { actionButtons: config, navButtons }
  }

  const { hasBulkDisciplineAccess } = navMenuAccess
  const {
    loadingModalOpen,
    loadingModalMessage,
    loadingModalAuxMessage,
    warningModalOpen,
    warningModalHeader,
    warningModalMessage,
    labelCollectorOpen,
  } = modalState
  const toolbarConfig = buildToolbarConfig(navMenuAccess)
  const sideMenuComponents = viewMode === "BULK_ACTIONS" ? buildSideMenuComponents() : <div />

  let tableData: any[] = []
  if (viewMode === "BULK_ACTIONS") {
    tableData = tableService.applySearch(searchString, bulkActionTasksTableData)
  } else if (viewMode === "PRESCRIPTION_REVIEW") {
    tableData = tableService.applySearch(searchString, prescriptionReviewData)
  } else {
    tableData = tableService.applySearch(searchString, profileData)
  }

  const tableColumnDef = tableService.convertPfColumnConfigToGridColDef(tableColumnConfig)
  const showRowSelection =
    viewMode === "PROFILE_SELECTION" || (viewMode === "BULK_ACTIONS" && selectedFilter === "IN PROGRESS")

  const handleRowClick = async (rowData: Record<string, any>) => {
    // Always open action flows modal when clicking on any table row
    await openActionFlowsModal(rowData)
  }

  const handleBulkActionRowSelection = (selectedIds: string[]) => {
    // Filter out any selected IDs that correspond to non-selectable rows
    const selectableIds = selectedIds.filter((id) => {
      const row = bulkActionTasksTableData.find((task: BulkDisciplineActionSchema) => task.id === id)
      if (!row) return false
      // Only allow selection for IN PROGRESS items
      return !row.isComplete && !row.isExpired
    })
    setSelectedBulkActionIds(selectableIds)
  }

  let actionFlowsModal = null
  if (actionFlowsModalOpen) {
    let modalTitle = "Disciplinary Action History"
    let modalSubtitle = ""

    if (viewMode === "PRESCRIPTION_REVIEW") {
      modalTitle = "Disciplinary Action"
      modalSubtitle = `Select an action from the options below`
    } else if (viewMode === "PROFILE_SELECTION") {
      modalTitle = "Disciplinary Action History"
      modalSubtitle = "Review existing discipline records"
    } else {
      modalTitle = "Disciplinary Action History"
      modalSubtitle = "Click on completed actions to view documents"
    }

    actionFlowsModal = (
      <Modal
        open={actionFlowsModalOpen}
        onBackdropClick={() => setActionFlowsModalOpen(false)}
        title={modalTitle}
        subTitle={modalSubtitle}
        maxWidth="xl"
        titleStyle={{ marginTop: 15 }}
        fullWidth={true}>
        <div
          style={{
            minHeight: 400,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            paddingBottom: 20,
          }}>
          <ActionFlows data={actionFlowsData} generateActionComponent={generateActionComponent} />
        </div>
      </Modal>
    )
  }

  let downloadQueueModal = null
  if (downloadQueueModalOpen) {
    downloadQueueModal = (
      <DownloadQueueModal
        open={downloadQueueModalOpen}
        downloadQueue={asyncTaskQueue}
        itemsDownloading={itemsDownloading}
        itemsBeingGenerated={itemsBeingGenerated}
        header="Bulk Discipline Document Queue"
        body=""
        isUpdatingDocExportQueue={isUpdatingQueue}
        updateQueueData={() => updateQueueData()}
        download={handleDownloadFile}
        dismiss={closeModals}
      />
    )
  }

  let documentViewerModal = null
  if (documentViewerModalOpen) {
    documentViewerModal = (
      <DocumentViewerModal
        document={getCurrentDocument()}
        currentDocumentIndex={currentDocumentIndex}
        totalDocuments={selectedDocumentNames.length}
        onClose={handleDocumentViewerClose}
        onNext={handleNextDocument}
        onPrevious={handlePreviousDocument}
        dismiss={handleDocumentViewerClose}
      />
    )
  }

  return (
    <div style={{ ...styles.container }}>
      <NavBar
        match={props.match}
        location={props.location}
        history={props.history}
        selectedProfilePks={selectedProfilePks}
      />
      <SectionHeader
        ref={sectionHeaderRef}
        style={styles.sectionHeader}
        searchString={searchString}
        textHandler={searchHandler}
        onClick={() => {
          // setSideMenuVisible(false)
        }}
        key={`sectionHeader_${selectedAssociation}`}>
        {titleCase(`${currentScreen}`)}
      </SectionHeader>

      <div style={styles.contentContainer}>
        <SideMenu visible={sideMenuVisible} menuComponents={sideMenuComponents} />
        <div
          style={{
            ...styles.rightSide,
            paddingInline: sideMenuVisible ? "max(2em, 2%)" : "min(3em, 3%)",
          }}>
          <Toolbar
            actionButtons={toolbarConfig.actionButtons}
            navButtons={toolbarConfig.navButtons}
            key={`actionToolbar_${toolbarConfig.actionButtons.length}`}
          />
          <div style={styles.rightSideContent} ref={primaryTableRef}>
            <Table
              rows={tableData}
              columns={tableColumnDef}
              tableHeight={tableHeight}
              rowSelection={showRowSelection}
              onRowClick={handleRowClick}
              onRowSelection={viewMode === "BULK_ACTIONS" ? handleBulkActionRowSelection : handleProfileSelection}
              selectedRowIds={viewMode === "PROFILE_SELECTION" ? selectedProfilePks : selectedBulkActionIds}
              key={`dataTable_${selectedAssociation}_${tableReloadTrigger}_${tableColumnConfig.length}_${viewMode}`}
            />
          </div>
        </div>
      </div>
      <AlertModalOneButton
        open={!hasBulkDisciplineAccess}
        header="Not Authorised"
        body="You don't have permission to access bulk discipline."
        buttonLabel="Ok"
        opaqueBackground={true}
        onClick={() => props.history.goBack()}
      />
      <AlertModalOneButton
        open={warningModalOpen}
        header={warningModalHeader}
        body={warningModalMessage}
        buttonLabel={"Ok"}
        onClick={() => closeModals()}
      />
      <LoadingModal
        open={loadingModalOpen}
        loadingMessage={loadingModalMessage}
        auxilliaryMessage={loadingModalAuxMessage}
      />
      <LabelCollector
        open={labelCollectorOpen}
        header="Offence selection"
        warning="Pick an offence"
        placeholder="Pick and offence"
        iconName="gavel"
        buttonLabel="Add"
        pickerItems={offenceOptions.map((offence) => removeUnderScores(offence))}
        submit={handleOffenceSelection}
        dismiss={closeLabelCollector}
      />
      {actionFlowsModal}
      {downloadQueueModal}
      {documentViewerModal}
      <AlertModalTwoButton
        open={alertTwoButtonModalOpen}
        header="Action Already Exists"
        body="This action has already been issued. Would you like to view the existing documents or issue a new action?"
        buttonLabel1="VIEW DOCUMENTS"
        buttonLabel2="ISSUE NEW"
        onClick1={handleViewExistingDocuments}
        onClick2={handleIssueNewDiscipline}
        dismiss={() => setAlertTwoButtonModalOpen(false)}
      />
    </div>
  )
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    backgroundImage: `linear-gradient(to bottom, ${ColorPalette.SCREEN_TOP_GRADIENT}, ${ColorPalette.SCREEN_BOTTOM_GRADIENT})`,
    height: "100vh",
  },
  sectionHeader: {
    margin: "3.5% auto 0.5%",
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    overflow: "auto",
  },
  rightSide: {
    display: "flex",
    flexDirection: "column" as "column",
    width: "100%",
    overflow: "hidden",
  },
  rightSideContent: {
    boxShadow: "0px -1px 8px rgba(60,60,60, 0.1)",
    display: "flex",
    flex: 1,
    backgroundColor: ColorPalette.CARD_WHITE,
  },
  sideMenuContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    width: "100%",
    overflow: "auto",
  },
  sideMenuIconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    marginTop: 15,
    marginBottom: 10,
  },
}

export default Radium(BulkDiscipline)
